import { StarbaseEntity, Serializable } from 'starbase-launcher';
import { LUSource } from './luSource';
import { IColumnModel, ColumnModel } from 'table-editor';
import { onlyUniqueByKey } from 'starbase-launcher';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ITableColumn, TableColumn, ColumnDataTypes, DropdownTableColumn, ColumnDropdownData, TypeaheadTableColumn } from 'new-table-editor';
import { EventEmitter } from '@angular/core';

export class CallNumberModel extends StarbaseEntity implements Serializable<CallNumberModel> {
  public lU_SourceUID: number;
  public lU_Source: LUSource;
  public source: string; // the name of the source for display
  public description: string;
  public callNumberL1: string;
  public callNumberL2: string;
  public descriptionL2: string;
  public callNumberL3: string;
  public descriptionL3: string;
  public callNumberL4: string;
  public descriptionL4: string;
  public imagesExist: boolean;
  public appears: number;

  public static CallNumberColumnsData(sources: LUSource[], editable: boolean = false): IColumnModel[] {
    sources = onlyUniqueByKey(sources, 'uid'); // distinct
    return [
      new ColumnModel('UID', 'uid', 'UID', false, 'number', false, false, 10, false, null, null),
      new ColumnModel('Source', 'lU_Source', 'LU_Source', editable, 'dropdown', true, true, 50, false, sources, 'source', null, null, null, null, false),
      new ColumnModel('Description', 'description', 'Description', editable, 'string', false, false, 500, true),
      new ColumnModel('Call# Level 1', 'callNumberL1', 'CallNumberL1', editable, 'string', false, false, 500, false),
      new ColumnModel('Call# Level 2', 'callNumberL2', 'CallNumberL2', editable, 'string', false, false, 500, false),
      new ColumnModel('Call# Level 3', 'callNumberL3', 'CallNumberL3', editable, 'string', false, false, 500, true),
      new ColumnModel('Call# Level 4', 'callNumberL4', 'CallNumberL4', editable, 'string', false, false, 500, true),
      new ColumnModel('Has Images?', 'imagesExist', 'ImagesExist', false, 'boolean', false, false, 1, false)
    ];
  }

  public static setupCallNumberCollection(collection: CallNumberModel[]): void {
    of(collection).pipe(
      map(
        (a: CallNumberModel[]) =>
          a.map((k: CallNumberModel) => {
            k.source = k.lU_Source.source;
            k = new CallNumberModel().deserialize(k);
            k.lU_Source = new LUSource().deserialize(k.lU_Source);
          })
      )
    ).subscribe();
  }

  public static callNoColumns(sources: LUSource[], typeaheadOptions: { l1: string[], l2: string[], l3: string[], l4: string[] }, editable: boolean = false): ITableColumn[] {
    const sourceCol = new DropdownTableColumn('lU_Source', 'Source', 'lU_SourceUID', new ColumnDropdownData(sources), true, editable, true);
    sourceCol.dropdownData.setup('source', 'uid');

    const callLevel1 = new TypeaheadTableColumn('callNumberL1', 'Call # Level 1', typeaheadOptions.l1, true, true, false);
    const callLevel2 = new TypeaheadTableColumn('callNumberL2', 'Call # Level 2', typeaheadOptions.l2, false, true, false);
    const callLevel3 = new TypeaheadTableColumn('callNumberL3', 'Call # Level 3', typeaheadOptions.l3, false, true, false);
    const callLevel4 = new TypeaheadTableColumn('callNumberL4', 'Call # Level 4', typeaheadOptions.l4, false, true, false);
    for(const cl of [callLevel1, callLevel2, callLevel3, callLevel4]) {
        cl.highlightOptions = {
        enabled: true,
        highlightColor: "#b01f23"
      };
    }

    return [
      sourceCol,
      callLevel1,
      new TableColumn('description', 'Description', ColumnDataTypes.string, false, true, false),
      callLevel2,
      new TableColumn('descriptionL2', 'Description', ColumnDataTypes.string, false, true, false),
      callLevel3,
      new TableColumn('descriptionL3', 'Description', ColumnDataTypes.string, false, true, false),
      callLevel4,
      new TableColumn('descriptionL4', 'Description', ColumnDataTypes.string, false, true, false),
      new TableColumn('imagesExist', 'Has Images?', ColumnDataTypes.yesno, false, false, false)
    ];
  }

  public static microfilmColumns(): ITableColumn[] {
    const columns = [
      new TableColumn('callNumberL1', 'Publication Number', ColumnDataTypes.string, true, false, true),
      new TableColumn('description', 'Description', ColumnDataTypes.string, true, false, false),
      new TableColumn('callNumberL2', 'Roll #', ColumnDataTypes.string, false, false, false),
      new TableColumn('descriptionL2', 'Roll Description', ColumnDataTypes.string, false, false, false),
      new TableColumn('appears', '# of Images', ColumnDataTypes.number, false, false, false)
    ];

    columns[0].colWidth = '15%';
    columns[1].colWidth = '30%';
    columns[2].colWidth = '15%';
    columns[3].colWidth = '30%';
    columns[4].colWidth = '10%';

    return columns;
  }

  public static microfilmColumnsMobile(): ITableColumn[] {
    const columns = [
      new TableColumn('callNumberL1', 'Publication Number', ColumnDataTypes.string, true, false, true),
      new TableColumn('callNumberL2', 'Roll #', ColumnDataTypes.string, false, false, false),
      new TableColumn('appears', '# of Images', ColumnDataTypes.number, false, false, false)
    ];

    columns[0].colWidth = '35%';
    columns[1].colWidth = '35%';
    columns[2].colWidth = '35%';

    return columns;
  }

  public static callSearchColumnsMobile(): ITableColumn[] {
    const columns = [
      new TableColumn('callNumberL1', 'Level 1', ColumnDataTypes.string, true, false, true),
      new TableColumn('callNumberL2', 'Level 2', ColumnDataTypes.string, false, false, false),
      new TableColumn('appears', '# of Images', ColumnDataTypes.number, false, false, false)
    ];

    columns[0].colWidth = '35%';
    columns[1].colWidth = '35%';
    columns[2].colWidth = '30%';

    return columns;
  }

  public static callSearchColumns(): ITableColumn[] {
    const columns = [
      new TableColumn('callNumberL1', 'Level 1', ColumnDataTypes.string, true, false, true),
      new TableColumn('description', 'Level 1 Description', ColumnDataTypes.string, true, false, false),
      new TableColumn('callNumberL2', 'Level 2', ColumnDataTypes.string, false, false, false),
      new TableColumn('descriptionL2', 'Level 2 Description', ColumnDataTypes.string, false, false, false),
      new TableColumn('appears', '# of Images', ColumnDataTypes.number, false, false, false)
    ];

    columns[0].colWidth = '15%';
    columns[1].colWidth = '30%';
    columns[2].colWidth = '15%';
    columns[3].colWidth = '30%';
    columns[4].colWidth = '10%';

    return columns;
  }

  deserialize(input) {
    return Object.assign(new CallNumberModel(), input);
  }
}
